import { agendaEventUpNextAriaLabel } from './AgendaEventViewUpNext.locstring.json';
import { TimeUntilView, getDurationString, getTimeUntilString } from 'owa-agenda-events-common';
import { PersonaDetailView } from './PersonaDetailView';
import { formatUserTime } from '../selectors/dateTimeSelectors';
import { isEventSoonOrNow } from 'owa-datetime-utils';
import { observer } from 'mobx-react-lite';
import { getCalendarEventIcons } from 'owa-calendar-event-icons';
import type { ClientItemId } from 'owa-client-ids';
import type { DisplayDate } from 'owa-datetime';
import loc, { format } from 'owa-localize';
import { observableNow } from 'owa-observable-datetime';
import * as React from 'react';
import {
    getAllInsightsFiles,
    getAllInsightsEmails,
    InsightDocSummary,
    hasInsightsToShowInAgendaViewUpNext,
    InsightsRenderLoggingHelper,
} from 'owa-meeting-insights';
import {
    isInsightsEnabledForEvent,
    InsightsDataFetchSource,
} from 'owa-meeting-insights-types-and-flights';
import {
    AgendaEventViewBase,
    Detail,
    AgendaEventViewBaseProps,
    CustomAgendaEventViewBaseStyles,
} from 'owa-agenda-event-views';
import { getPalette } from 'owa-theme';
import { logUsage } from 'owa-analytics';
import { getDensityModeString } from 'owa-fabric-theme';
import {
    getIconFromProviderName,
    getJoinMeetingLabelFromProviderName,
} from 'owa-online-meeting/lib/utils/onlineMeetingHelper';
import type { OnlineMeetingProviderType } from 'owa-online-meeting/lib/utils/OnlineMeetingProviderType';

import styles from './AgendaEventViewUpNext.scss';
import commonStyles from './common.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface UpNextProps {
    id: ClientItemId;
    startTime: DisplayDate;
    endTime: DisplayDate;
    organizerEmail: string;
    organizerName: string;
    location: string;
    subject: string;
    isCancelled: boolean;
    isMeeting: boolean;
    className: string;
    provider: string;
    joinUrl: string;
    hasAttachments: boolean;
    isRecurring: boolean;
    isPrivate: boolean;
    isRecurringException: boolean;
    charmId: number;
    isOrganizer: boolean;
    onJoinOnlineMeetingClick: (target: any) => void;
    customStyles: CustomAgendaEventUpNextStyles;
}

export interface CustomAgendaEventUpNextStyles extends CustomAgendaEventViewBaseStyles {
    upNext: {
        joinButton: string;
        subjectDetailContainer: string;
        outerContainer: string;
        timeContainer: string;
        agendaEventContainer: string;
    };
}

export type AgendaEventViewUpNextProps = Omit<
    AgendaEventViewBaseProps,
    'topDetails' | 'bottomDetails'
> &
    UpNextProps;

function withAgendaEventViewUpNextProps(
    AgendaEventViewBase: React.ComponentType<AgendaEventViewBaseProps>
): React.ComponentType<AgendaEventViewUpNextProps> {
    const AgendaViewWithUpNext: React.FunctionComponent<
        AgendaEventViewBaseProps & UpNextProps
    > = props => {
        const {
            startTime,
            endTime,
            isCancelled,
            subject,
            location,
            organizerEmail,
            organizerName,
            isMeeting,
            className,
            joinUrl,
            provider,
            hasAttachments,
            isRecurring,
            isPrivate,
            isRecurringException,
            charmId,
            ariaLabel,
            showCompact,
            customStyles,
            disableCharmColors,
            ...passThroughProps
        } = props;
        const details: Detail[] = [];
        const eventId = props.id.Id;
        const colorScheme = passThroughProps.colorScheme;

        const isInsightsEnabled = isInsightsEnabledForEvent(eventId);
        const hasInsightsToShow = hasInsightsToShowInAgendaViewUpNext(eventId);
        const isCompactDensity =
            showCompact !== undefined ? showCompact : getDensityModeString() === 'compact';
        const showJoinButton = joinUrl && isEventSoonOrNow(startTime, observableNow());

        React.useEffect(() => {
            // This datapoint is for Meeting Insights experiments in order to measure "denominator" of agenda up-next renders
            logUsage('AgendaEventViewUpNextRendered', { isCore: true, excludeFromKusto: true });
        }, []);

        const getJoinButtonProps = () => {
            const icon = getIconFromProviderName(provider as OnlineMeetingProviderType);
            const providerString = getJoinMeetingLabelFromProviderName(
                provider as OnlineMeetingProviderType,
                isCompactDensity
            );
            const whiteColor = getPalette().white + '!important';
            const calColor = colorScheme
                ? colorScheme.accessibleText.msColorWhiteBackground + '!important'
                : null;
            const darkerCalColor = colorScheme
                ? colorScheme.accessibleText.calLighterBackground + '!important'
                : null;
            const iconProps = isCompactDensity
                ? null
                : {
                      iconName: icon,
                      styles: { root: styles.providerIcon },
                  };
            const buttonStyle = isCompactDensity
                ? {
                      root: {
                          color: whiteColor,
                          borderColor: calColor,
                          backgroundColor: calColor,
                      },
                      rootHovered: {
                          color: whiteColor,
                          borderColor: darkerCalColor,
                          backgroundColor: darkerCalColor,
                      },
                      textContainer: styles.buttonText,
                      label: styles.buttonLabel,
                      position: 'absolute',
                      flexContainer: {
                          height: 'inherit',
                      },
                  }
                : {
                      root: {
                          color: calColor,
                          borderColor: calColor,
                          backgroundColor: whiteColor,
                      },
                      rootHovered: {
                          color: whiteColor,
                          borderColor: calColor,
                          backgroundColor: calColor,
                      },
                      textContainer: styles.buttonText,
                      label: styles.buttonLabel,
                  };

            return {
                href: joinUrl,
                iconProps: iconProps,
                target: '_blank',
                text: providerString,
                onClick: props.onJoinOnlineMeetingClick,
                className: classNames(styles.button, customStyles?.upNext.joinButton, {
                    compact: isCompactDensity,
                }),
                styles: buttonStyle,
            };
        };

        const renderInsights = React.useCallback(() => {
            const files = getAllInsightsFiles(eventId, InsightsDataFetchSource.AgendaView);
            const emails = getAllInsightsEmails(eventId, InsightsDataFetchSource.AgendaView);
            return (
                <InsightDocSummary
                    colorScheme={colorScheme}
                    meetingId={eventId}
                    files={files}
                    emails={emails}
                    compactDocClass={styles.compactDoc}
                    entrySource={InsightsDataFetchSource.AgendaView}
                />
            );
        }, [eventId, colorScheme]);

        details.push({
            getTextProps: () => ({
                text: subject,
                className: classNames(styles.subject, customStyles?.common.subject, {
                    isCancelled: isCancelled,
                    compact: isCompactDensity,
                }),
            }),
            getCharmControllProps: charmId
                ? () => ({ iconId: charmId, className: styles.charm })
                : null,
            colorScheme: disableCharmColors ? undefined : colorScheme,
            className: classNames(
                styles.subjectDetailContainer,
                customStyles?.upNext.subjectDetailContainer
            ),
        });
        if (location) {
            details.push({
                getTextProps: () => ({
                    text: location,
                    className: classNames(
                        {
                            [styles.locationCompact]: showJoinButton && isCompactDensity,
                        },
                        customStyles?.common.location
                    ),
                }),
            });
        }
        if (isMeeting && organizerEmail) {
            details.push({
                renderCustomDetailPart: () => (
                    <PersonaDetailView
                        organizerEmail={organizerEmail}
                        organizerName={organizerName}
                    />
                ),
            });
        }

        let smallButtonDetail;
        if (showJoinButton && isCompactDensity) {
            smallButtonDetail = {
                buttonProps: getJoinButtonProps(),
                colorScheme: colorScheme,
                isFullDetail: true,
            };
        }
        const topDetails = {
            timeDetails: [
                {
                    getTextProps: () => ({
                        text: formatUserTime(startTime),
                        className: classNames(
                            commonStyles.timePrimaryText,
                            customStyles?.common.time
                        ),
                    }),
                },
                {
                    getTextProps: () => ({
                        text: getDurationString(startTime, endTime),
                        className: classNames(
                            commonStyles.timeSecondaryText,
                            customStyles?.common.time
                        ),
                    }),
                },
            ],
            icons: getCalendarEventIcons(
                !hasInsightsToShow && hasAttachments,
                isRecurring,
                isPrivate,
                isRecurringException
            ),
            details: details,
            buttonDetail: smallButtonDetail,
        };
        const bottomDetails = [];
        if (hasInsightsToShow) {
            bottomDetails.push({
                renderCustomDetailPart: renderInsights,
            });
        }

        if (showJoinButton && !isCompactDensity) {
            const buttonDetail = {
                buttonProps: getJoinButtonProps(),
                colorScheme: colorScheme,
                isFullDetail: true,
            };
            bottomDetails.push(buttonDetail);
        }

        return (
            <>
                {isInsightsEnabled && (
                    <InsightsRenderLoggingHelper
                        eventId={eventId}
                        source={InsightsDataFetchSource.AgendaView}
                        hasInsightsToShow={hasInsightsToShow}
                    />
                )}
                <div
                    className={classNames(
                        styles.outerContainer,
                        customStyles?.upNext.outerContainer
                    )}>
                    <TimeUntilView
                        startDate={startTime}
                        endDate={endTime}
                        containerClassName={classNames(
                            styles.timeContainer,
                            customStyles?.upNext.timeContainer
                        )}
                    />
                    <AgendaEventViewBase
                        {...passThroughProps}
                        showCompact={showCompact}
                        customStyles={customStyles}
                        topDetails={topDetails}
                        bottomDetails={bottomDetails}
                        className={classNames(
                            styles.agendaEventContainer,
                            customStyles?.upNext.agendaEventContainer,
                            className
                        )}
                        // adjust the event aria-label to include the information from `TimeUntilView`
                        ariaLabel={format(
                            loc(agendaEventUpNextAriaLabel),
                            getTimeUntilString(startTime, endTime, observableNow()),
                            ariaLabel
                        )}
                    />
                </div>
            </>
        );
    };

    return observer(AgendaViewWithUpNext);
}

export const AgendaEventViewUpNext = withAgendaEventViewUpNextProps(AgendaEventViewBase);
